import { MouseEvent, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ToggleOption } from '@anytag/shared-types';
import { useToggleState } from '@anytag/shared-hooks';
import { WarningModal } from '@anytag/shared-molecules';
import { MarketplaceCampaignStatus } from '@anytag/graphql';
import { Form, BaseModal, ButtonSubmit } from '@anytag/shared-atoms';
import { MarketplaceFormValues } from '../types';
import { compareChangedValues } from '../utils';

interface FormTemplateProps {
  children: ReactNode;
  hideSubmit?: boolean;
  onSubmit?: () => void;
  defaultValues: MarketplaceFormValues;
}

export const FormLayoutTemplate = ({ children, onSubmit, defaultValues, hideSubmit }: FormTemplateProps) => {
  const { t } = useTranslation();
  const warningModal = useToggleState();
  const allowNewInfluencerModal = useToggleState();
  const {
    getValues,
    formState: { isSubmitting }
  } = useFormContext<MarketplaceFormValues>();

  const values = getValues();
  const isEditMode = !!values.id;
  const isAllowNewInfluencer = values.allowNewInfluencer === ToggleOption.TRUE;

  const handleClickSave = (e: MouseEvent<HTMLButtonElement>) => {
    if (isEditMode) {
      const hasSensitiveChanges = compareChangedValues(defaultValues, values);
      if (hasSensitiveChanges && values.status === MarketplaceCampaignStatus.ONGOING) {
        e.preventDefault();
        warningModal.open();

        return;
      }

      if (defaultValues.allowNewInfluencer !== values.allowNewInfluencer) {
        e.preventDefault();
        allowNewInfluencerModal.open();

        return;
      }
    }

    onSubmit?.();
  };

  const handleSubmitWarningModal = () => {
    onSubmit?.();
    warningModal.close();
  };

  const handleSubmitAllowNewInfluencerModal = () => {
    onSubmit?.();
    allowNewInfluencerModal.close();
  };

  return (
    <>
      <Form.Body className="form-container">{children}</Form.Body>

      {!hideSubmit ? (
        <Form.Footer>
          <ButtonSubmit
            title={t('Button.Save')}
            loading={isSubmitting}
            onClick={handleClickSave}
            id="mkp-save-button"
          />
        </Form.Footer>
      ) : null}

      {isEditMode ? (
        <>
          <WarningModal
            open={warningModal.status}
            onClose={warningModal.close}
            title={t('Dialog.Edit Ongoing Campaign')}
            footer={{ submitButton: { title: t('Button.Update'), onClick: handleSubmitWarningModal } }}
          />

          <BaseModal
            open={allowNewInfluencerModal.status}
            onClose={allowNewInfluencerModal.close}
            title={t(isAllowNewInfluencer ? 'Modal.saveAsNormalCampaignTitle' : 'Modal.saveAsClosedCampaignTitle')}
            description={t(
              isAllowNewInfluencer ? 'Modal.saveAsNormalCampaignDescription' : 'Modal.saveAsClosedCampaignDescription'
            )}
            footer={{
              submitButton: {
                loading: isSubmitting,
                title: t('Button.Save'),
                onClick: handleSubmitAllowNewInfluencerModal
              }
            }}
          />
        </>
      ) : null}
    </>
  );
};
